import { STUDIO_AI_NAME } from '@/@studio-ai/constants';
import { StudioRoutes } from '@/app/routes';

/**
 * Pages where the floating Studio AI assistant should not appear.
 *
 * An entry matches the path itself and everything beneath it, compared segment
 * by segment. A `:param` segment matches any single segment, so a route with a
 * dynamic slug in the middle can be named — `/reports` stays enabled while
 * `/reports/anything/build` does not.
 */
export const ASSISTANT_DISABLED_PAGES: string[] = [
    StudioRoutes.ACCOUNT,
    StudioRoutes.STUDIO_AI,
    StudioRoutes.STUDIO_AI_SANDBOX,
    StudioRoutes.CROSSTABS,
    StudioRoutes.ADMIN,
    StudioRoutes.BUILDER,
    // The home screen has its own inline "ask DANI" composer. A second chat
    // input floating at the bottom of the screen reads as a second assistant.
    StudioRoutes.HOME,
    // The guided report builder is already a conversation with DANI. A second
    // chat input at the bottom of the screen reads as a second assistant.
    `${StudioRoutes.REPORTS}/:templateSlug/build`,
];

function toSegments(value: string): string[] {
    return value.split('/').filter(Boolean);
}

/** True when `pathname` is, or lives under, one of `patterns`. */
export function isAssistantDisabledPage(
    pathname: string,
    patterns: string[] = ASSISTANT_DISABLED_PAGES,
): boolean {
    const pathSegments = toSegments(pathname);
    return patterns.some((pattern) => {
        const patternSegments = toSegments(pattern);
        if (patternSegments.length === 0) return false;
        // Prefix match at a segment boundary — never a partial segment, so
        // `/tools` must not match `/toolshed`.
        if (patternSegments.length > pathSegments.length) return false;
        return patternSegments.every(
            (segment, i) => segment.startsWith(':') || segment === pathSegments[i],
        );
    });
}

export const PERSISTENT_ASSISTANT_CONFIG = {
    SIZE: 56,
    Z_INDEX: 1250,
    /** Below HeroUI's modal backdrop (z-50) so open modals dim the assistant. */
    Z_INDEX_UNDER_MODAL: 40,
} as const;

export const ASSISTANT_CHAT_INPUT_MESSAGES = {
    MAIN: `Hello, I'm ${STUDIO_AI_NAME}, your Data Analytics and Navigation Instructor.`,
    MAIN_SUBTITLE: 'What consumer connections can I help you explore and discover?',
    /** Shown before the user sends their first message. */
    DEFAULT_PLACEHOLDER: `Hi ${STUDIO_AI_NAME}, what insights can you give me?`,
    /** Shown after at least one message exists in the conversation. */
    FOLLOW_UP_PLACEHOLDER: 'Ask a follow-up question...',
    /** Page-specific placeholders keyed by route prefix. */
    PAGE_PLACEHOLDERS: {
        [StudioRoutes.MY_SURVEYS]: 'Ask a question about your surveys...',
        [StudioRoutes.SURVEY_PULSE]: 'Ask a question about survey pulse data...',
        [StudioRoutes.CROSSTABS]: 'Ask a question about your crosstabs...',
    } as Record<string, string>,
};
